import type { FieldValues, UseFormSetError } from 'react-hook-form';
import useIsStepByStepEnabled from '~/client/components/Auth/Authenticate/AuthSteps/hooks/useIsStepByStepEnabled';
import useRouter from '~/client/hooks/router/useRouter';
import usePhoneNumberHint from '~/client/hooks/usePhoneNumberHint';
import type { UserManagementError } from '~/schema/public';
import Cookie from '~/shared/utils/Cookie';
import useWalletErrors from './useCheckWalletErrors';

type Args<T extends FieldValues> = {
  setError?: UseFormSetError<T>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  redirectUrl?: string;
  errors: UserManagementError[] | undefined;
  address: string;
};

const useCheckWalletAuthErrors = (authMethod?: WalletAuthMethod) => {
  const { push } = useRouter();
  const { hasSMS2FAErrors, hasOtpErrors, hasAgeLimitErrors } =
    useWalletErrors();
  const { setPhoneNumberHint } = usePhoneNumberHint();
  const isStepByStepEnabled = useIsStepByStepEnabled();

  const walletAuthMethod = authMethod || 'metamask';

  const handleAuthErrors = <T extends FieldValues>({
    errors,
    setError,
    setErrors,
    setOtpError,
    redirectUrl,
    address,
  }: Args<T>) => {
    const redirect = redirectUrl?.length ? `&redirect=${redirectUrl}` : '';

    if (!errors) {
      return null;
    }

    setErrors?.(errors);
    setPhoneNumberHint(errors);

    const error = errors[0];
    const isSMS2FAError = hasSMS2FAErrors(error);
    const isOtpError = hasOtpErrors(error);
    const { error: isDateOfBirthError, message: dateOfBirthErrorMessage } =
      hasAgeLimitErrors(error);
    if (isSMS2FAError) {
      Cookie.set('loginError', error.message);
      if (isStepByStepEnabled) {
        return push(`/authenticate?redirect=${redirectUrl}`);
      }
      return push(`/login?redirect=${redirectUrl}`, undefined);
    }
    if (isOtpError) {
      Cookie.set('loginError', error.message);
      setOtpError?.();
      if (isStepByStepEnabled) {
        return push(`/authenticate?redirect=${redirectUrl}`);
      }
      return push(
        `/two-factor-authentication?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }
    if (error.params?.[0]?.value?.includes('password')) {
      if (isStepByStepEnabled) {
        return push(
          `/authenticate?${walletAuthMethod}=true&addr=${address}${redirect}`,
        );
      }
      return push(
        `/complete-registration?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }

    if (isDateOfBirthError) {
      setError?.('dateOfBirth.day' as never, {
        message: dateOfBirthErrorMessage,
      });
      if (isStepByStepEnabled) {
        return push(`/authenticate?redirect=${redirectUrl}`);
      }
      return push(
        `/complete-registration?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }
    return null;
  };

  return { handleAuthErrors };
};

export default useCheckWalletAuthErrors;
